import React, { useState } from 'react';
import { Box, Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { auth } from '../firebase';
import { sendEmailVerification } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { ensureBackendUser } from '../utils/utils';
import { getOnboardingRedirectPath } from '../utils/onboardingNavigation';
import { useUser } from '../context/UserContext';

const VerifyEmailPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();
  const { refreshFirebaseUser, loadUserContext } = useUser();

  const handleResend = async () => {
    setError(null);
    setStatus(null);
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (user) await sendEmailVerification(user);
      setStatus("Verification email sent. Check your inbox and spam folder.");
    } catch {
      setError("Could not resend verification. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleUseDifferentAccount = async () => {
    setError(null);
    setStatus(null);
    try {
      await auth.signOut();
    } catch {
      // Signing out locally is enough to get back to the login form.
    }
    navigate("/auth/login", { replace: true });
  };

   const handleContinue = async () => {
        setError(null);
        setStatus(null);
        setLoading(true);

        try {
            if (!auth.currentUser) {
            setError("No authenticated user.");
            return;
            }

            const refreshed = await refreshFirebaseUser();
            if (!refreshed?.emailVerified) {
            setError("Still waiting on your verification. Check your inbox!");
            return;
            }

            await ensureBackendUser();
            const ctx = await loadUserContext();

            // Returning users who already finished onboarding shouldn't be
            // walked through setup again just because they verified late.
            navigate(getOnboardingRedirectPath(ctx || null), { replace: true });
        } catch (err) {
            console.error("handleContinue error:", err);
            setError(
            err.response?.data?.error ||
            err.message ||
            "Could not create your account. Please try again."
            );
        } finally {
            setLoading(false);
        }
        };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8, textAlign: 'center' }}>
      <Typography variant="h6" gutterBottom>
        Verify Your Email
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        We sent a verification link to <strong>{auth.currentUser?.email}</strong>. 
        Click it in your inbox to continue.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {status && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {status}
        </Alert>
      )}
      <Stack spacing={2}>
        <Button
          variant="contained"
          onClick={handleResend}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : "Resend Email"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleContinue}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : "I’ve Verified – Continue"}
        </Button>
        <Button
          variant="text"
          onClick={handleUseDifferentAccount}
          disabled={loading}
          sx={{ color: '#334155' }}
        >
          Use a different account
        </Button>
      </Stack>
    </Box>
  );
};

export default VerifyEmailPage;